/**
 * Step Engine interazioni hotspot (multi-step) — MULTI-OPEN (più hotspot aperti).
 *
 * Requisiti:
 * - hotspotLayer (da createHotspotLayer)
 * - hotspotLayer.getElement(id) -> dot element
 * - hotspotLayer.getHotspot(id) -> dati hotspot (con interactions[])
 */
export function createInteractions({
  containerEl,
  hotspotLayer,
  activeClass = "is-active",
  dotSelector = ".hs-dot",
  tooltipClass = "hs-tooltip",
  defaultText = "Informazione hotspot",
  offsetPx = 12,
  onSelect = () => {},
  onDeselect = () => {},
} = {}) {
  if (!containerEl) throw new Error("createInteractions: containerEl mancante");
  if (!hotspotLayer) throw new Error("createInteractions: hotspotLayer mancante");

  // Stato per ogni hotspot aperto
  // id(string) -> { id, stepIndex, tooltipEl, typingTimer, typingRunId }
  const open = new Map();

  function stopEvt(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  function getDotById(id) {
    return hotspotLayer.getElement(id);
  }

  function getHotspotById(id) {
    const hs = hotspotLayer.getHotspot?.(id);
    if (hs) return hs;

    const el = getDotById(id);
    if (!el) return null;
    return { id, text: el.dataset.text || defaultText, interactions: [] };
  }

  function getStepsForHotspot(hs) {
    const steps = Array.isArray(hs?.interactions) ? hs.interactions.slice() : [];
    steps.sort((a, b) => {
      const pa = Number.isFinite(a?.position) ? a.position : 999999;
      const pb = Number.isFinite(b?.position) ? b.position : 999999;
      return pa - pb;
    });
    return steps;
  }

  function positionTooltipForDot(tooltipEl, dotEl) {
    const rect = dotEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    const x = rect.left - containerRect.left + rect.width / 2;
    const y = rect.top - containerRect.top;

    tooltipEl.style.left = `${x}px`;
    tooltipEl.style.top = `${y - offsetPx}px`;
  }

  // ---------
  // TYPEWRITER (per-tooltip)
  // ---------
  function stopTyping(state) {
    state.typingRunId += 1;
    if (state.typingTimer) {
      clearInterval(state.typingTimer);
      state.typingTimer = null;
    }
  }

  function startTypewriter(state, { targetEl, fullText, mode = "word", speedMs = null }) {
    stopTyping(state);

    const runId = state.typingRunId;
    const text = String(fullText || "");

    if (!text.length) {
      targetEl.textContent = "";
      return;
    }

    const m = mode === "char" ? "char" : "word";
    const interval =
      typeof speedMs === "number" && Number.isFinite(speedMs)
        ? Math.max(8, speedMs)
        : m === "char"
          ? 22
          : 120;

    const tokens =
      m === "char"
        ? Array.from(text)
        : text.split(/(\s+)/).filter((t) => t.length > 0);

    let i = 0;
    targetEl.textContent = "";

    state.typingTimer = setInterval(() => {
      if (runId !== state.typingRunId) return;

      if (i >= tokens.length) {
        clearInterval(state.typingTimer);
        state.typingTimer = null;
        return;
      }

      targetEl.textContent += tokens[i];
      i += 1;
    }, interval);
  }

  function renderNav(state, { hasPrev, hasNext, onPrev, onNext }) {
    const nav = document.createElement("div");
    nav.className = "hs-tooltip__nav";

    const btnPrev = document.createElement("button");
    btnPrev.type = "button";
    btnPrev.className = "hs-tooltip__btn hs-tooltip__btn--prev";
    btnPrev.textContent = "◀";
    btnPrev.disabled = !hasPrev;

    const btnNext = document.createElement("button");
    btnNext.type = "button";
    btnNext.className = "hs-tooltip__btn hs-tooltip__btn--next";
    btnNext.textContent = "▶";
    btnNext.disabled = !hasNext;

    btnPrev.addEventListener("pointerdown", stopEvt);
    btnPrev.addEventListener("pointerup", stopEvt);
    btnNext.addEventListener("pointerdown", stopEvt);
    btnNext.addEventListener("pointerup", stopEvt);

    btnPrev.addEventListener("click", (e) => {
      stopEvt(e);
      onPrev?.();
    });
    btnNext.addEventListener("click", (e) => {
      stopEvt(e);
      onNext?.();
    });

    nav.appendChild(btnPrev);
    nav.appendChild(btnNext);
    return nav;
  }

  function renderTooltipStep(state, step) {
    const wrap = document.createElement("div");
    wrap.className = "hs-tooltip__content";

    if (step?.title) {
      const h = document.createElement("div");
      h.className = "hs-tooltip__title";
      h.textContent = step.title;
      wrap.appendChild(h);
    }

    const text = step?.content || defaultText;

    // usa il tuo flag: config.mode
    const mode = step?.config?.mode || "indirect";
    const isDirect = mode === "direct";

    const twMode = step?.config?.typewriter_mode || "word";
    const twSpeed = step?.config?.typewriter_speed;

    const p = document.createElement("div");
    p.className = "hs-tooltip__text";

    if (isDirect) {
      const openQ = document.createElement("span");
      openQ.className = "hs-tooltip__quote hs-tooltip__quote--open";
      openQ.textContent = "« ";

      const closeQ = document.createElement("span");
      closeQ.className = "hs-tooltip__quote hs-tooltip__quote--close";
      closeQ.textContent = " »";

      const em = document.createElement("em");
      em.className = "hs-tooltip__speech";

      p.appendChild(openQ);
      p.appendChild(em);
      p.appendChild(closeQ);

      startTypewriter(state, {
        targetEl: em,
        fullText: text,
        mode: twMode,
        speedMs: twSpeed,
      });
    } else {
      const span = document.createElement("span");
      span.className = "hs-tooltip__plain";
      span.textContent = text;
      p.appendChild(span);
    }

    wrap.appendChild(p);
    return wrap;
  }

  function renderMultipleChoiceStep(step, { onAnswered }) {
    const wrap = document.createElement("div");
    wrap.className = "hs-tooltip__content";

    if (step?.title) {
      const h = document.createElement("div");
      h.className = "hs-tooltip__title";
      h.textContent = step.title;
      wrap.appendChild(h);
    }

    const q = document.createElement("div");
    q.className = "hs-tooltip__text";
    q.textContent = step?.content || "Scegli una risposta:";
    wrap.appendChild(q);

    const answers = step?.config?.answers;
    const list = Array.isArray(answers) ? answers : [];

    const btns = document.createElement("div");
    btns.className = "hs-tooltip__choices";

    if (list.length === 0) {
      const warn = document.createElement("div");
      warn.className = "hs-tooltip__warn";
      warn.textContent = "⚠ Nessuna risposta configurata (config.answers).";
      btns.appendChild(warn);
    } else {
      list.forEach((a, idx) => {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "hs-tooltip__choice";
        b.textContent = a?.text ?? `Risposta ${idx + 1}`;

        b.addEventListener("pointerdown", stopEvt);
        b.addEventListener("pointerup", stopEvt);

        b.addEventListener("click", (e) => {
          stopEvt(e);
          const correct = !!a?.correct;
          onAnswered?.({ index: idx, correct, answer: a });
        });

        btns.appendChild(b);
      });
    }

    wrap.appendChild(btns);

    const feedback = document.createElement("div");
    feedback.className = "hs-tooltip__feedback";
    wrap.appendChild(feedback);

    return { wrap, feedback };
  }

  function renderPlaceholder(step, label) {
    const wrap = document.createElement("div");
    wrap.className = "hs-tooltip__content";

    const h = document.createElement("div");
    h.className = "hs-tooltip__title";
    h.textContent = step?.title || "Interazione";
    wrap.appendChild(h);

    const p = document.createElement("div");
    p.className = "hs-tooltip__text";
    p.textContent = `(${label}) non ancora implementato.`;
    wrap.appendChild(p);

    return wrap;
  }

  function renderActiveStepFor(state) {
    const dotEl = getDotById(state.id);
    if (!dotEl) return;

    const hs = getHotspotById(state.id);
    if (!hs) return;

    const steps = getStepsForHotspot(hs);
    if (steps.length === 0) {
      clearActive(state.id);
      return;
    }

    stopTyping(state);

    state.stepIndex = Math.max(0, Math.min(state.stepIndex, steps.length - 1));
    const step = steps[state.stepIndex];

    state.tooltipEl.innerHTML = "";

    if (step.kind === "tooltip") {
      state.tooltipEl.appendChild(renderTooltipStep(state, step));
    } else if (step.kind === "multiple_choice") {
      const { wrap, feedback } = renderMultipleChoiceStep(step, {
        onAnswered: ({ correct }) => {
          feedback.textContent = correct ? "✅ Corretto" : "❌ Errato";
        },
      });
      state.tooltipEl.appendChild(wrap);
    } else if (step.kind === "tool_selection") {
      state.tooltipEl.appendChild(renderPlaceholder(step, "tool_selection"));
    } else if (step.kind === "ordered_tools") {
      state.tooltipEl.appendChild(renderPlaceholder(step, "ordered_tools"));
    } else {
      state.tooltipEl.appendChild(renderPlaceholder(step, step.kind || "unknown"));
    }

    if (steps.length > 1) {
      const hasPrev = state.stepIndex > 0;
      const hasNext = state.stepIndex < steps.length - 1;

      state.tooltipEl.appendChild(
        renderNav(state, {
          hasPrev,
          hasNext,
          onPrev: () => {
            state.stepIndex -= 1;
            renderActiveStepFor(state);
          },
          onNext: () => {
            state.stepIndex += 1;
            renderActiveStepFor(state);
          },
        })
      );
    }

    state.tooltipEl.style.display = "block";
    positionTooltipForDot(state.tooltipEl, dotEl);

    onSelect({
      id: hs.id,
      hotspot: hs,
      step,
      stepIndex: state.stepIndex,
      stepsCount: steps.length,
      element: dotEl,
    });
  }

  function setActive(id) {
    const key = String(id);
    if (open.has(key)) return;

    const dotEl = getDotById(id);
    if (!dotEl) return;

    dotEl.classList.add(activeClass);

    const t = document.createElement("div");
    t.className = tooltipClass;
    t.style.display = "none";
    t.style.pointerEvents = "auto";

    // blocca chiusure/drag quando clicchi dentro
    t.addEventListener("pointerdown", (e) => e.stopPropagation());
    t.addEventListener("pointerup", (e) => e.stopPropagation());
    t.addEventListener("click", (e) => e.stopPropagation());

    containerEl.appendChild(t);

    const state = {
      id,
      stepIndex: 0,
      tooltipEl: t,
      typingTimer: null,
      typingRunId: 0,
    };

    open.set(key, state);
    renderActiveStepFor(state);
  }

  function clearActive(id) {
    const key = String(id);
    const state = open.get(key);
    if (!state) return;

    stopTyping(state);

    const dotEl = getDotById(id);
    dotEl?.classList.remove(activeClass);

    state.tooltipEl.remove();
    open.delete(key);

    onDeselect({ id });
  }

  function toggleActive(id) {
    const key = String(id);
    if (open.has(key)) clearActive(id);
    else setActive(id);
  }

  function update() {
    // aggiorna posizione di TUTTE le tooltip aperte
    for (const state of open.values()) {
      const dotEl = getDotById(state.id);
      if (!dotEl) continue;

      const isHidden = window.getComputedStyle(dotEl).display === "none";
      if (isHidden) {
        state.tooltipEl.style.display = "none";
        continue;
      }

      positionTooltipForDot(state.tooltipEl, dotEl);
      state.tooltipEl.style.display = "block";
    }
  }

  // Toggle SOLO su hotspot
  function onContainerClick(e) {
    const target = e.target;

    // se click dentro una tooltip, ignora
    if (target?.closest?.(`.${tooltipClass}`)) return;

    const dot = target?.closest?.(dotSelector);
    if (!dot || !containerEl.contains(dot)) return;

    stopEvt(e);

    const id = parseInt(dot.dataset.hotspotId, 10);
    if (!id) return;

    toggleActive(id);
  }

  containerEl.addEventListener("click", onContainerClick);

  function onKeyDown(e) {
    // ESC: chiude tutte
    if (e.code === "Escape") {
      for (const state of Array.from(open.values())) clearActive(state.id);
    }
  }
  window.addEventListener("keydown", onKeyDown);

  return {
    setActive,
    clearActive,
    toggleActive,
    update,
    destroy() {
      containerEl.removeEventListener("click", onContainerClick);
      window.removeEventListener("keydown", onKeyDown);
      for (const state of Array.from(open.values())) clearActive(state.id);
    },
    get openIds() {
      return Array.from(open.keys());
    },
  };
}